import DOMPurify from "dompurify";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalAtom from "../../../../components/atoms/modal/modal";
import {
	formatQuantityToLocale,
	formatValueToLocale,
} from "../../../../utils/method";
import "./ElementDetailsModal.scss";

const TYPE_KEY = {
	ServiceSpecificationGroupDto: "group",
	PositionDto: "position",
	NoteTextDto: "note",
};

const Stat = ({ label, value, highlight = false, mono = true }) => (
	<div className={`edm-stat${highlight ? " edm-stat--highlight" : ""}`}>
		<div className="edm-stat-label">{label}</div>
		<div className={`edm-stat-value${mono ? " edm-stat-value--mono" : ""}`}>
			{value}
		</div>
	</div>
);

const ElementDetailsModal = ({ element, show, onHide }) => {
	const { t } = useTranslation();
	const [zIndex, setZIndex] = useState(1050);

	useEffect(() => {
		if (show) {
			setZIndex((prev) => prev + 10);
		}
	}, [show]);

	if (!element) return null;

	const typeKey = TYPE_KEY[element.elementType] || "details";
	const isNote = typeKey === "note";
	const itemNumber = element?.itemNumber?.stringRepresentation || "";
	const sanitizedLongText = element.longText
		? DOMPurify.sanitize(element.htmlLongText || element.longText)
		: null;

	const title = `${t(`element-details.${typeKey}`)}${itemNumber ? ` ${itemNumber}` : ""}`;

	const body = (
		<div className="edm-body">
			{/* Type chip + item number - keeps context visible while body scrolls */}
			<div className="edm-chip-row">
				<span className={`edm-type-chip edm-type-chip--${typeKey}`}>
					{t(`element-details.${typeKey}`)}
				</span>
				{itemNumber && (
					<span className="edm-item-number">{itemNumber}</span>
				)}
			</div>

			{/* Short text */}
			<section className="edm-section">
				<h3 className="edm-section-title">
					{t("element-details.short-text")}
				</h3>
				{element.shortText ? (
					<p className="edm-short-text">{element.shortText}</p>
				) : (
					<p className="edm-empty">{t("invoice.no-description")}</p>
				)}
			</section>

			{/* Long text - preserves HTML formatting from GAEB */}
			{sanitizedLongText && (
				<section className="edm-section">
					<h3 className="edm-section-title">
						{t("element-details.long-text")}
					</h3>
					<div
						className="edm-long-text"
						dangerouslySetInnerHTML={{ __html: sanitizedLongText }}
					/>
				</section>
			)}

			{/* Stats - hidden for notes */}
			{!isNote && (
				<section className="edm-section">
					<div className="edm-stats-grid">
						<Stat
							label={t("element-details.quantity")}
							value={
								element.quantity
									? `${formatQuantityToLocale(element.quantity)} ${element.unitTag || ""}`
									: "-"
							}
						/>
						<Stat
							label={t("element-details.unit-price")}
							value={
								element.unitPrice != null
									? formatValueToLocale(element.unitPrice, true)
									: "-"
							}
						/>
						<Stat
							label={t("element-details.total-price")}
							value={formatValueToLocale(element.totalPrice, true)}
							highlight
						/>
						<Stat
							label={t("element-details.gross-total-price")}
							value={formatValueToLocale(
								element.totalPriceGrossDeducted,
								true,
							)}
							highlight
						/>
					</div>
				</section>
			)}
		</div>
	);

	return (
		<ModalAtom
			show={show}
			handleClose={onHide}
			title={title}
			body={body}
			centered
			hideSaveButton
			customClassName="element-details-modal"
			size="lg"
			style={{ zIndex }}
			backdropClassName="modal-backdrop-blur"
		/>
	);
};

export default ElementDetailsModal;
